import { useEffect, useRef, useState } from 'react';
import { useCreateComment, useCreateNote } from '../../data/hooks';
import { Button, Dialog, Textarea } from '../../ui';

/**
 * WRITING HAPPENS IN A SHEET, THE SAME ONE EVERY OTHER SHORT ENTRY IN THIS PRODUCT USES.
 *
 * THE CAUSE OF THE THREE THINGS THE OWNER FOUND (2026-09-17, C3-1: "abgeschnitten", "nur
 * mit diesem Icon zu bedienen", "sieht leicht anders aus als die anderen Popup-Fenster").
 * Until this round the desktop composer was a NON-MODAL PANEL positioned inside the record
 * pane - `.record-panel` in index.css, `position: fixed` at the pane's bottom trailing
 * corner with a fixed `--record-panel-width` - so at his window size the panel ran past the
 * window edge and was cut; the only thing that resized it was the browser's own textarea
 * grip, which `ui/Textarea` ships as `resize-y`; and its chrome was a hand-built title row
 * plus footer rather than the sheet chrome every other dialog on the screen wears. Three
 * defects, one construction behind them.
 *
 * SO IT IS `ui/Dialog` WITH `variant="auto"` NOW, and nothing here draws chrome. Apple:
 * "A sheet is a modal view that appears attached to the window" and a sheet is dismissed
 * through its own buttons (hig:
 * https://developer.apple.com/design/human-interface-guidelines/sheets). The kit's own
 * instance of that view is the save sheet, and it is where the shape comes from: a sheet
 * box of 390x318 with a 20 content inset on every side, and a button row 350x24 at the
 * trailing edge of that inset with the leading button separated from the trailing pair.
 * kit: Apple macOS 27 UI Kit.sketch / Dialogs/Save Dialog Light / Save Dialog - Collapsed,
 * whose Buttons group sits at (20,274). The product's shared `ui/Dialog` already draws
 * exactly that row, which is the whole argument for not drawing another one here.
 *
 * ON THE PHONE NOTHING CHANGES. `variant="auto"` renders the sheet shape unchanged below
 * the desktop breakpoint - `BottomSheet` was `Dialog variant="sheet"` and the two shapes are
 * the same declaration there - so the rising sheet, its top action bar and its dismissals
 * are exactly what they were.
 *
 * ONE WAY OUT, AND IT IS THE SHEET'S OWN PAIR. `confirm` puts Cancel on the leading side and
 * the verb as the DEFAULT button on the trailing side, which is what the HIG's sheets page
 * asks for, and it binds Return to the verb everywhere except inside a text area - so
 * Return still starts a new line in the note being written.
 *
 * THE TEXT AREA HAS NO GRIP AND A FIXED HEIGHT. `resize: none` removes the browser's corner
 * handle, which was never a product affordance; `rows={6}` is the height, unchanged from the
 * panel, so the sheet is a fixed comfortable box rather than something to be dragged.
 *
 * WAVE 4 (C2-12): IT WRITES COMMENTS TOO. A comment was a resizable textarea with a
 * "Comment" push button under it, pinned to the bottom of the thread - a web form on a
 * screen whose every other surface is a kit part, and the third place this product knew
 * how to start a piece of writing. `spec/screens/record.md` section 2 item 7 has ONE
 * construction for writing on this record, so the kind decides the sheet's noun title and
 * its verb and nothing else changes: "New Note" with "Add Note", "New Comment" with
 * "Add Comment".
 *
 * FORGIVENESS (rulebook F2): Escape, Cancel and a click on the dimmed window close a clean
 * sheet at once; with something typed they ask "Keep this note?", because the alternative is
 * a composer that silently eats a call summary somebody just wrote. The question is this
 * component's own and not `Dialog`'s `dirty` prompt, because the answer here is three-way -
 * keep editing, discard, or add it to the record - and a discard prompt offers two.
 */
export type ComposerKind = 'note' | 'comment';

/** The noun the sheet is titled with, and the verb its default button carries. */
const WORDS: Record<ComposerKind, { readonly noun: string; readonly verb: string }> = {
  note: { noun: 'New note', verb: 'Add note' },
  comment: { noun: 'New comment', verb: 'Add comment' },
};

export interface NoteComposerProps {
  readonly objectId: string;
  readonly recordId: string;
  /**
   * DEFAULTS TO `note`, so the two callers that only ever write notes - the record page's
   * toolbar and the list pane's - say nothing about a kind they do not have.
   */
  readonly kind?: ComposerKind | undefined;
  /** `comment` only: the note this comment hangs off, when it is a note's own thread. */
  readonly noteId?: string | undefined;
  /** `comment` only: the comment being answered (notes-tasks-comments.md 2.4, one level). */
  readonly replyToCommentId?: string | undefined;
  /** `comment` only: whose comment is being answered, for the panel's own line. */
  readonly replyToName?: string | undefined;
  readonly open: boolean;
  /**
   * Bumped by every toolbar request so a second press RETURNS to the draft instead of
   * remounting it (S1-4's behaviour, kept through the move into the panel). Zero means
   * "nobody asked", so an unfocused panel is a caller that never bumped it.
   */
  readonly focusRequest?: number | undefined;
  readonly onOpenChange: (open: boolean) => void;
}

export function NoteComposer({
  objectId,
  recordId,
  kind = 'note',
  noteId,
  replyToCommentId,
  replyToName,
  open,
  focusRequest = 0,
  onOpenChange,
}: NoteComposerProps) {
  const createNote = useCreateNote(objectId, recordId);
  const createComment = useCreateComment(objectId, recordId);
  const words = WORDS[kind];
  const pending = kind === 'note' ? createNote.isPending : createComment.isPending;
  const [draft, setDraft] = useState('');
  const [asking, setAsking] = useState(false);
  const editor = useRef<HTMLTextAreaElement>(null);

  /**
   * The REQUEST is what focuses, not the opening: a second press of New Note has to
   * return to the draft that is already there, and re-running on `open` alone would
   * do nothing because `open` did not change.
   */
  useEffect(() => {
    if (open && focusRequest > 0) editor.current?.focus();
  }, [open, focusRequest]);

  const close = (): void => {
    setDraft('');
    setAsking(false);
    onOpenChange(false);
  };
  const submit = (): void => {
    const trimmed = draft.trim();
    if (trimmed === '') return;
    if (kind === 'note') {
      createNote.mutate(trimmed);
    } else {
      createComment.mutate({
        body: trimmed,
        ...(noteId === undefined ? {} : { noteId }),
        ...(replyToCommentId === undefined ? {} : { replyToCommentId }),
      });
    }
    close();
  };
  /** Clean closes; dirty asks. One sheet at a time, so one question at a time. */
  const requestClose = (): void => {
    if (draft.trim() === '') close();
    else setAsking(true);
  };

  const placeholder = kind === 'note' ? 'Write a note' : 'Write a comment';

  const question = (
    <Dialog
      alert
      open={asking}
      onOpenChange={setAsking}
      title={kind === 'note' ? 'Keep this note?' : 'Keep this comment?'}
      description="You have written something that has not been added to the record yet."
      width="sm"
      nested
      footer={
        <>
          <Button variant="ghost" onClick={() => setAsking(false)}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={close}>
            Discard
          </Button>
          <Button variant="secondary" onClick={submit}>
            {words.verb}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-muted">Adding it keeps it on the record's timeline.</p>
    </Dialog>
  );

  return (
    <>
      <Dialog
        variant="auto"
        open={open}
        /* Escape, Cancel and the dimmed window all arrive here, so the question is asked
           once for all three rather than once per control. */
        onOpenChange={(next) => {
          if (next) onOpenChange(true);
          else requestClose();
        }}
        title={words.noun}
        width="md"
        confirm={{
          label: words.verb,
          onConfirm: submit,
          disabled: draft.trim() === '',
          loading: pending,
        }}
      >
        {/* WHO IS BEING ANSWERED, as the sheet's own first line rather than as a chip above
            a textarea: a reply is a comment with a parent, and the parent is context the
            writer needs while writing. It is the only thing the two kinds do not share. */}
        {replyToName !== undefined && (
          <p className="record-composer-context">{`Replying to ${replyToName}`}</p>
        )}
        <Textarea
          ref={editor}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          rows={6}
          /* THE GRIP IS GONE (C3-1). `ui/Textarea` ships `resize-y` for the fields that
             grow with their content; here the browser's corner handle was the only way to
             change a panel the window had already clipped, which is the affordance the
             owner found. An inline style rather than a utility class, because `resize-y`
             and `resize-none` are the same utility group and which one wins would depend
             on the order Tailwind happens to emit them in. */
          style={{ resize: 'none' }}
        />
      </Dialog>
      {question}
    </>
  );
}
